import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useParams, useNavigate } from "react-router-dom";
import productActions from "../../../store/actions/items/item.actions";
import { SkeletonList } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const ManageProductImagesPageComponent = ({ productActions }) => {
  const { productId } = useParams(); // Get productId from URL
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [primaryImageId, setPrimaryImageId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch product images when the page loads
  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const fetchProductDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const product = await productActions.getProductById(productId);
      const fetchedImages = product.payload.images;
      setImages(fetchedImages);

      // Determine the primary image based on the product's primaryImageId
      if (product.payload.primaryImageId) {
        setPrimaryImageId(product.payload.primaryImageId);
      } else {
        setPrimaryImageId(null);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddImage = async () => {
    if (!selectedFile) {
      alert("Please select a file.");
      return;
    }

    try {
      const addImageResponse = await productActions.addProductImage(productId, selectedFile, false);
        if(addImageResponse.statusCode === 200) {
            fetchProductDetails();
            setSelectedFile(null); // Reset file input
        }
    } catch (error) {
      console.error("Error adding product image:", error);
      setError(error);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      await productActions.deleteProductImage(productId, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      if (imageId === primaryImageId) setPrimaryImageId(null);
    } catch (error) {
      console.error("Error deleting product image:", error);
      setError(error);
    }
  };

  const handleSetPrimary = async (imageId) => {
    try {
      await productActions.setImageAsPrimary(productId, imageId);
      setPrimaryImageId(imageId);
      setImages((prev) =>
        prev.map((img) => ({
          ...img,
          isPrimary: img.id === imageId,
        }))
      );
    } catch (error) {
      console.error("Error setting primary image:", error);
      setError(error);
    }
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  return (
    <div>
      <h2>Manage Product Images</h2>

      {isLoading ? (
        showSkeleton && <SkeletonList rows={4} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <>
          <div>
            <label>Add New Image:</label>
            <input type="file" onChange={handleFileChange} />
            <button onClick={handleAddImage}>Add Image</button>
          </div>
          <div>
            <h3>Current Images</h3>
            <ul>
              {images.map((img) => (
                <li key={img.id} style={{ marginBottom: "10px" }}>
                  <img src={img.url} alt="Product" style={{ width: "100px", height: "100px" }} />
                  <div>
                    <button onClick={() => handleSetPrimary(img.id)} disabled={img.id === primaryImageId}>
                      {img.id === primaryImageId ? "Primary Image" : "Set as Primary"}
                    </button>
                    <button onClick={() => handleDeleteImage(img.id)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <button onClick={() => navigate(-1)}>Back</button>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    productActions: bindActionCreators(productActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(ManageProductImagesPageComponent);
